import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs/internal/operators/take';
import { ManagementSentimentControllerService, ManagementSentimentResponse } from 'api-clipping';
import { SharedService } from '../../services/shared.service';
import { CORPORATE_TOPICS, INDUSTRY_TOPICS, SOURCE_TYPES } from '../../interfaces/taxonomy-data';
import { translateSourceType, translateTopic } from '../../../client/components/analytics/shared/taxonomy.util';

interface InputData {
	sentiment: ManagementSentimentResponse;
}

@Component({
	selector: 'app-sentiment-taxonomy-dialog',
	standalone: true,
	imports: [
		FormsModule,
		MatDialogModule,
		MatIconModule,
		MatButtonModule,
		MatSelectModule,
		MatFormFieldModule,
		MatCheckboxModule,
		MatProgressSpinnerModule,
		MatDividerModule,
		TranslateModule,
	],
	templateUrl: './sentiment-taxonomy-dialog.component.html',
	styleUrl: './sentiment-taxonomy-dialog.component.scss',
})
export class SentimentTaxonomyDialogComponent {
	readonly sourceTypes = SOURCE_TYPES;
	readonly industryTopics = INDUSTRY_TOPICS;
	readonly corporateTopics = CORPORATE_TOPICS;

	// '' represents "no Source Type" in the select (its own option, distinct from leaving the
	// field untouched - this dialog always sends the full desired state).
	sourceType: string = '';
	selectedIndustryTopics: string[] = [];
	selectedCorporateTopics: string[] = [];

	saving = false;

	constructor(
		public dialogRef: MatDialogRef<SentimentTaxonomyDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private apiClippingSentiment: ManagementSentimentControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		this.sourceType = data.sentiment.sourceType || '';
		this.selectedIndustryTopics = [...(data.sentiment.industryTopics || [])];
		this.selectedCorporateTopics = [...(data.sentiment.corporateTopics || [])];
	}

	// Underlying values/model stay the fixed English labels (AiTaxonomy.java, taxonomy-data.ts) -
	// only the displayed text is translated, same mapping the Analytics screens use.
	translateSourceTypeLabel(source: string | null | undefined): string {
		return translateSourceType(source, this.translate);
	}

	translateTopicLabel(topic: string | null | undefined): string {
		return translateTopic(topic, this.translate);
	}

	toggleIndustryTopic(topic: string, checked: boolean) {
		if (checked) {
			if (this.selectedIndustryTopics.length < 2 && !this.selectedIndustryTopics.includes(topic)) {
				this.selectedIndustryTopics = [...this.selectedIndustryTopics, topic];
			}
		} else {
			this.selectedIndustryTopics = this.selectedIndustryTopics.filter(t => t !== topic);
		}
	}

	toggleCorporateTopic(topic: string, checked: boolean) {
		if (checked) {
			if (this.selectedCorporateTopics.length < 2 && !this.selectedCorporateTopics.includes(topic)) {
				this.selectedCorporateTopics = [...this.selectedCorporateTopics, topic];
			}
		} else {
			this.selectedCorporateTopics = this.selectedCorporateTopics.filter(t => t !== topic);
		}
	}

	save() {
		if (!this.data.sentiment.id) {
			return;
		}
		this.saving = true;
		this.apiClippingSentiment
			.updateSentimentTaxonomy(
				{
					sourceType: this.sourceType || undefined,
					industryTopics: this.selectedIndustryTopics,
					corporateTopics: this.selectedCorporateTopics,
				},
				this.data.sentiment.id
			)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.dialogRef.close(response);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
					this.saving = false;
				},
			});
	}

	closeDialog() {
		this.dialogRef.close();
	}
}
